import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import type { ColumnDef } from '@tanstack/react-table';
import { Bookmark, ChevronDown, ChevronRight, Layers, MessageSquare, Paperclip, Plus, Settings2, ShieldAlert, Trash2, X } from 'lucide-react';
import type { SavedView, Task, TaskBulkRequest, TaskBulkResult, TaskList, TaskStatus, TaskTopic, TaskTopicCreate, TaskTopicList, TaskTopicUpdate } from '@mnc/contracts';
import { api, qs } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { describeError } from '../../lib/errors';
import { useParameterOptions } from '../../lib/parameters';
import { useListPrefs, usePreferences } from '../../lib/prefs';
import { pick } from '../../i18n';
import { DataTable } from '../../components/DataTable';
import { Badge, ConfirmDialog, Field, Modal, PageHeader, SearchableSelect, cn } from '../../components/ui';
import { UserPicker, VisibilityBadge } from '../../components/records';
import { parseTaskSearch, type TaskSearch } from './search';

const STATUSES: TaskStatus[] = ['todo', 'in_progress', 'blocked', 'done', 'cancelled'];
const VIEWS = ['ongoing', 'overdue', 'done', 'all'] as const;

export function statusTone(s: TaskStatus): 'neutral' | 'brand' | 'warn' | 'danger' | 'info' {
  return s === 'done' ? 'brand' : s === 'blocked' ? 'danger' : s === 'in_progress' ? 'info' : s === 'cancelled' ? 'neutral' : 'neutral';
}

export function TasksPage() {
  const { t, i18n } = useTranslation();
  const { can, canAction } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const search = parseTaskSearch(useSearch({ strict: false }) as Record<string, unknown>);
  const { listPrefs, setListPrefs, views: savedViews } = useListPrefs('tasks', { page_size: 25 });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [topicsOpen, setTopicsOpen] = useState(false);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [viewName, setViewName] = useState('');
  const { update: updatePrefs } = usePreferences();

  const view = search.view ?? 'ongoing';
  const sort = search.sort ?? listPrefs.sort ?? '';
  const pageSize = listPrefs.page_size ?? 25;
  const page = search.page ?? 1;
  const assignees = search.assignees ? search.assignees.split(',') : [];
  const grouped = search.grouped === '1';

  const setSearch = (patch: Partial<TaskSearch>, resetPage = true) => {
    const next: Record<string, unknown> = { ...search, ...patch };
    if (resetPage) delete next.page;
    for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === '' || next[k] === false) delete next[k];
    void navigate({ to: '/tasks', search: next as never });
    setSelected(new Set());
  };

  const query = {
    view,
    q: search.q,
    assignees: search.assignees,
    mine: search.mine === '1' ? true : undefined,
    pillar_code: search.pillar,
    urgency_code: search.urgency,
    follow_up_with_code: search.follow_up,
    topic_id: search.topic,
    sort: sort || undefined,
    page,
    page_size: pageSize,
  };
  const list = useQuery({ queryKey: ['tasks', query], queryFn: () => api.get<TaskList>(`/api/tasks${qs(query)}`), placeholderData: (prev) => prev });
  const topics = useQuery({ queryKey: ['task-topics'], queryFn: () => api.get<TaskTopicList>('/api/tasks/topics'), staleTime: 60_000 });
  const pillar = useParameterOptions('pillar');
  const urgency = useParameterOptions('urgency');
  const followUp = useParameterOptions('follow_up_with');
  const topicOptions = useMemo(() => [{ value: 'none', label: t('tasks.noTopic') }, ...(topics.data?.items ?? []).map((tp) => ({ value: tp.id, label: pick(i18n.language, tp.name_en, tp.name_ar) }))], [topics.data, i18n.language, t]);
  const topicById = useMemo(() => new Map((topics.data?.items ?? []).map((tp) => [tp.id, tp])), [topics.data]);

  const bulk = useMutation({
    mutationFn: (body: TaskBulkRequest) => api.post<TaskBulkResult>('/api/tasks/bulk', body),
    onSuccess: (r) => {
      toast.success(t('tasks.bulkDone', { updated: r.updated, skipped: r.skipped.length }));
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (e) => toast.error(describeError(e, t)),
  });

  const columns = useMemo<ColumnDef<Task, unknown>[]>(
    () => [
      {
        id: 'select',
        enableSorting: false,
        header: () => <input type="checkbox" aria-label="select all" checked={Boolean(list.data?.items.length) && list.data!.items.every((i) => selected.has(i.id))} onChange={(e) => setSelected(e.target.checked ? new Set(list.data?.items.map((i) => i.id)) : new Set())} />,
        cell: ({ row }) => <input type="checkbox" checked={selected.has(row.original.id)} onClick={(e) => e.stopPropagation()} onChange={(e) => setSelected((s) => { const n = new Set(s); if (e.target.checked) n.add(row.original.id); else n.delete(row.original.id); return n; })} />,
      },
      { id: 'seq', header: '#', accessorKey: 'seq', cell: ({ row }) => <span className="font-mono text-xs text-neutral-500">#{row.original.seq}</span> },
      {
        id: 'title',
        header: t('tasks.title'),
        accessorKey: 'title',
        cell: ({ row }) => {
          const task = row.original;
          const topic = task.topic_id ? topicById.get(task.topic_id) : undefined;
          return (
            <div className="min-w-56">
              <div className="flex flex-wrap items-center gap-1 font-medium" dir="auto">
                {task.title}
                {task.is_confidential && <Badge tone="danger"><ShieldAlert size={10} /> {t('tasks.confidential')}</Badge>}
                <VisibilityBadge visibility={task.visibility} />
              </div>
              {!grouped && topic && <span className="mt-0.5 inline-flex items-center gap-1 text-xs text-neutral-500"><span className="h-2 w-2 rounded-full" style={{ background: topic.colour }} />{pick(i18n.language, topic.name_en, topic.name_ar)}</span>}
            </div>
          );
        },
      },
      { id: 'pillar', header: t('tasks.pillar'), accessorFn: (r) => r.pillar_code ?? '', cell: ({ row }) => <span dir="auto">{pillar.labelOf(row.original.pillar_code)}</span> },
      { id: 'urgency', header: t('tasks.urgency'), accessorFn: (r) => r.urgency_code ?? '', cell: ({ row }) => row.original.urgency_code ? <Badge tone={row.original.urgency_code === 'high' ? 'danger' : row.original.urgency_code === 'medium' ? 'warn' : 'neutral'}>{urgency.labelOf(row.original.urgency_code)}</Badge> : null },
      { id: 'status', header: t('tasks.status'), accessorKey: 'status', cell: ({ row }) => <Badge tone={statusTone(row.original.status)}>{t(`tasks.statuses.${row.original.status}`)}</Badge> },
      { id: 'due_date', header: t('tasks.dueDate'), accessorKey: 'due_date', cell: ({ row }) => row.original.due_date ? <span className={cn('whitespace-nowrap text-xs', row.original.is_overdue && 'font-semibold text-red-600')}>{new Date(row.original.due_date).toLocaleDateString(i18n.language)}{row.original.is_overdue && ` · ${t('tasks.overdue')}`}</span> : <span className="text-neutral-400">—</span> },
      {
        id: 'team',
        header: t('tasks.team'),
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.owner && <Badge tone="brand">{pick(i18n.language, row.original.owner.display_name_en, row.original.owner.display_name_ar)}</Badge>}
            {row.original.team.map((p) => <Badge key={p.id}>{pick(i18n.language, p.display_name_en, p.display_name_ar)}</Badge>)}
          </div>
        ),
      },
      { id: 'activity', header: '', enableSorting: false, cell: ({ row }) => <span className="flex items-center gap-2 text-xs text-neutral-500">{row.original.note_count > 0 && <span className="inline-flex items-center gap-0.5"><MessageSquare size={12} />{row.original.note_count}</span>}{row.original.attachment_count > 0 && <span className="inline-flex items-center gap-0.5"><Paperclip size={12} />{row.original.attachment_count}</span>}</span> },
    ],
    [t, i18n.language, list.data, selected, grouped, topicById, pillar, urgency],
  );

  const items = list.data?.items ?? [];
  const groups = useMemo(() => {
    const map = new Map<string, { topic: TaskTopic | null; rows: Task[] }>();
    for (const task of items) {
      const key = task.topic_id ?? 'none';
      if (!map.has(key)) map.set(key, { topic: task.topic_id ? (topicById.get(task.topic_id) ?? null) : null, rows: [] });
      map.get(key)!.rows.push(task);
    }
    return [...map.entries()].sort(([a], [b]) => (a === 'none' ? 1 : b === 'none' ? -1 : 0));
  }, [items, topicById]);

  const openTask = (task: Task) => void navigate({ to: '/tasks/$id', params: { id: task.id } });
  const applyView = (v: SavedView) => setSearch({ ...(v.filters as Partial<TaskSearch>), sort: v.sort ?? undefined });
  const saveView = async () => {
    const id = `${Date.now().toString(36)}`;
    const filters: Partial<TaskSearch> = { view, q: search.q, assignees: search.assignees, mine: search.mine, pillar: search.pillar, urgency: search.urgency, follow_up: search.follow_up, topic: search.topic, grouped: search.grouped };
    await updatePrefs({ views: { tasks: [...savedViews, { id, name: viewName.trim(), filters: filters as Record<string, unknown>, sort: sort || null, columns: null }] } });
    setSaveViewOpen(false);
    setViewName('');
  };
  const deleteView = (id: string) => void updatePrefs({ views: { tasks: savedViews.filter((v) => v.id !== id) } });

  const pageMeta = list.data ? { ...list.data.meta, onChange: (p: number, s: number) => { if (s !== pageSize) setListPrefs({ page_size: s }); setSearch({ page: p > 1 ? p : undefined }, false); } } : undefined;
  const sortCtl = { value: sort || (list.data?.meta.sort ?? ''), onChange: (s: string) => { setListPrefs({ sort: s }); setSearch({ sort: s || undefined }); } };

  return (
    <div>
      <PageHeader
        title={t('tasks.pageTitle')}
        actions={
          <>
            <button type="button" className="btn-secondary" onClick={() => setTopicsOpen(true)}><Settings2 size={16} /> {t('tasks.topics')}</button>
            {can('tasks', 'can_create') && <button type="button" className="btn-primary" onClick={() => void navigate({ to: '/tasks/new', search: {} })}><Plus size={16} /> {t('tasks.new')}</button>}
          </>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex overflow-hidden rounded-lg border border-neutral-300 text-sm dark:border-neutral-700">
          {VIEWS.map((v) => (
            <button key={v} type="button" onClick={() => setSearch({ view: v === 'ongoing' ? undefined : v })} className={cn('px-3 py-1.5', view === v ? 'bg-brand-600 text-white' : 'bg-white dark:bg-neutral-900')}>
              {t(`tasks.views.${v}`)}{list.data && <span className="ms-1 text-xs opacity-70">{list.data.counts[v]}</span>}
            </button>
          ))}
        </div>
        {savedViews.map((v) => (
          <span key={v.id} className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-1 text-xs dark:bg-neutral-800">
            <button type="button" onClick={() => applyView(v)} className="inline-flex items-center gap-1"><Bookmark size={12} /> {v.name}</button>
            <button type="button" onClick={() => deleteView(v.id)} aria-label={t('app.delete')}><X size={12} /></button>
          </span>
        ))}
        <button type="button" className="btn-secondary px-2 py-1 text-xs" onClick={() => setSaveViewOpen(true)}><Bookmark size={12} /> {t('tasks.saveView')}</button>
      </div>

      <div className="mb-3 grid gap-2 md:grid-cols-6">
        <input className="input md:col-span-2" placeholder={t('tasks.searchPlaceholder')} defaultValue={search.q ?? ''} onKeyDown={(e) => { if (e.key === 'Enter') setSearch({ q: (e.target as HTMLInputElement).value || undefined }); }} onBlur={(e) => { if ((e.target.value || undefined) !== search.q) setSearch({ q: e.target.value || undefined }); }} />
        <UserPicker value={assignees} onChange={(ids) => setSearch({ assignees: ids.length ? ids.join(',') : undefined })} />
        <SearchableSelect value={search.pillar ?? ''} onChange={(v) => setSearch({ pillar: (v as string) || undefined })} options={[{ value: '', label: t('tasks.anyPillar') }, ...pillar.options]} placeholder={t('tasks.pillar')} />
        <SearchableSelect value={search.urgency ?? ''} onChange={(v) => setSearch({ urgency: (v as string) || undefined })} options={[{ value: '', label: t('tasks.anyUrgency') }, ...urgency.options]} placeholder={t('tasks.urgency')} />
        <SearchableSelect value={search.topic ?? ''} onChange={(v) => setSearch({ topic: (v as string) || undefined })} options={[{ value: '', label: t('tasks.anyTopic') }, ...topicOptions]} placeholder={t('tasks.topic')} />
        <div className="flex flex-wrap items-center gap-3 text-sm md:col-span-6">
          {followUp.options.length > 0 && <div className="w-56"><SearchableSelect value={search.follow_up ?? ''} onChange={(v) => setSearch({ follow_up: (v as string) || undefined })} options={[{ value: '', label: t('tasks.anyFollowUp') }, ...followUp.options]} placeholder={t('tasks.followUp')} /></div>}
          <label className="flex items-center gap-2"><input type="checkbox" checked={search.mine === '1'} onChange={(e) => setSearch({ mine: e.target.checked ? '1' : undefined })} />{t('tasks.mine')}</label>
          <button type="button" className={cn('btn-secondary px-2 py-1 text-xs', grouped && 'bg-brand-50 text-brand-700 dark:bg-brand-700/30 dark:text-brand-100')} onClick={() => setSearch({ grouped: grouped ? undefined : '1' }, false)}><Layers size={14} /> {t('tasks.groupByTopic')}</button>
          {grouped && <button type="button" className="btn-secondary px-2 py-1 text-xs" onClick={() => setCollapsed(collapsed.size ? new Set() : new Set(groups.map(([k]) => k)))}>{collapsed.size ? t('tasks.expandAll') : t('tasks.collapseAll')}</button>}
        </div>
      </div>

      {selected.size > 0 && can('tasks', 'can_edit') && (
        <BulkBar count={selected.size} onClear={() => setSelected(new Set())} onRun={(body) => bulk.mutate({ ...body, ids: [...selected] })} topicOptions={topicOptions.filter((o) => o.value !== 'none')} urgencyOptions={urgency.options} />
      )}

      {!grouped && <DataTable columns={columns} data={items} loading={list.isLoading} onRowClick={openTask} sort={sortCtl} page={pageMeta} />}
      {grouped && (
        <div className="grid gap-3">
          {groups.map(([key, g]) => {
            const isCollapsed = collapsed.has(key);
            return (
              <div key={key} className="card">
                <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-start text-sm font-semibold" onClick={() => setCollapsed((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; })}>
                  {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                  <span className="h-3 w-3 rounded-full" style={{ background: g.topic?.colour ?? '#9ca3af' }} />
                  <span dir="auto">{g.topic ? pick(i18n.language, g.topic.name_en, g.topic.name_ar) : t('tasks.noTopic')}</span>
                  <span className="text-xs font-normal text-neutral-500">({g.rows.length})</span>
                </button>
                {!isCollapsed && <DataTable columns={columns} data={g.rows} onRowClick={openTask} sort={sortCtl} />}
              </div>
            );
          })}
          {groups.length === 0 && !list.isLoading && <div className="card p-10 text-center text-sm text-neutral-500">{t('app.noResults')}</div>}
          {pageMeta && pageMeta.total > pageSize && (
            <div className="flex items-center justify-end gap-2 text-xs">
              <button type="button" className="btn-secondary px-2 py-1" disabled={page <= 1} onClick={() => pageMeta.onChange(page - 1, pageSize)}>{t('app.previous')}</button>
              <span>{page} / {Math.max(1, Math.ceil(pageMeta.total / pageSize))}</span>
              <button type="button" className="btn-secondary px-2 py-1" disabled={page >= Math.ceil(pageMeta.total / pageSize)} onClick={() => pageMeta.onChange(page + 1, pageSize)}>{t('app.next')}</button>
            </div>
          )}
        </div>
      )}

      <Modal open={saveViewOpen} onClose={() => setSaveViewOpen(false)} title={t('tasks.saveView')} footer={<><button type="button" className="btn-secondary" onClick={() => setSaveViewOpen(false)}>{t('app.cancel')}</button><button type="button" className="btn-primary" disabled={!viewName.trim()} onClick={() => void saveView()}>{t('app.save')}</button></>}>
        <Field label={t('tasks.viewName')} required><input className="input" value={viewName} onChange={(e) => setViewName(e.target.value)} autoFocus /></Field>
      </Modal>
      {topicsOpen && <TopicsModal onClose={() => setTopicsOpen(false)} canDelete={canAction('tasks.manage_topics')} canEdit={can('tasks', 'can_edit')} />}
    </div>
  );
}

function BulkBar({ count, onClear, onRun, topicOptions, urgencyOptions }: { count: number; onClear: () => void; onRun: (body: Omit<TaskBulkRequest, 'ids'>) => void; topicOptions: Array<{ value: string; label: string }>; urgencyOptions: Array<{ value: string; label: string }> }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<TaskStatus | ''>('');
  const [assign, setAssign] = useState<string[]>([]);
  const [topic, setTopic] = useState('');
  const [urg, setUrg] = useState('');
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-brand-100 bg-brand-50 p-2 text-sm dark:border-brand-700/40 dark:bg-brand-700/20">
      <span className="font-medium">{t('tasks.selected', { count })}</span>
      <div className="w-40"><SearchableSelect value={status} onChange={(v) => setStatus(v as TaskStatus)} options={STATUSES.map((s) => ({ value: s, label: t(`tasks.statuses.${s}`) }))} placeholder={t('tasks.status')} /></div>
      <button type="button" className="btn-secondary px-2 py-1" disabled={!status} onClick={() => status && onRun({ action: 'set_status', status })}>{t('tasks.applyStatus')}</button>
      <div className="w-56"><UserPicker value={assign} onChange={setAssign} /></div>
      <button type="button" className="btn-secondary px-2 py-1" disabled={!assign.length} onClick={() => onRun({ action: 'add_team', user_ids: assign })}>{t('tasks.assign')}</button>
      <div className="w-44"><SearchableSelect value={topic} onChange={(v) => setTopic(v as string)} options={topicOptions} placeholder={t('tasks.topic')} /></div>
      <button type="button" className="btn-secondary px-2 py-1" disabled={!topic} onClick={() => onRun({ action: 'move_topic', topic_id: topic })}>{t('tasks.moveTopic')}</button>
      <div className="w-36"><SearchableSelect value={urg} onChange={(v) => setUrg(v as string)} options={urgencyOptions} placeholder={t('tasks.urgency')} /></div>
      <button type="button" className="btn-secondary px-2 py-1" disabled={!urg} onClick={() => onRun({ action: 'set_urgency', urgency_code: urg })}>{t('tasks.setUrgency')}</button>
      <button type="button" className="ms-auto btn-secondary px-2 py-1" onClick={onClear}><X size={14} /> {t('app.cancel')}</button>
    </div>
  );
}

function TopicsModal({ onClose, canEdit, canDelete }: { onClose: () => void; canEdit: boolean; canDelete: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const topics = useQuery({ queryKey: ['task-topics', 'all'], queryFn: () => api.get<TaskTopicList>('/api/tasks/topics?include_inactive=true') });
  const [editing, setEditing] = useState<TaskTopic | 'new' | null>(null);
  const [form, setForm] = useState<{ name_en: string; name_ar: string; description: string; colour: string }>({ name_en: '', name_ar: '', description: '', colour: '#6366f1' });
  const [toDelete, setToDelete] = useState<TaskTopic | null>(null);
  const invalidate = () => { void qc.invalidateQueries({ queryKey: ['task-topics'] }); void qc.invalidateQueries({ queryKey: ['tasks'] }); };
  const create = useMutation({ mutationFn: (b: TaskTopicCreate) => api.post<TaskTopic>('/api/tasks/topics', b), onSuccess: () => { toast.success(t('tasks.topicSaved')); setEditing(null); invalidate(); }, onError: (e) => toast.error(describeError(e, t)) });
  const update = useMutation({ mutationFn: ({ id, b }: { id: string; b: TaskTopicUpdate }) => api.patch<TaskTopic>(`/api/tasks/topics/${id}`, b), onSuccess: () => { toast.success(t('tasks.topicSaved')); setEditing(null); invalidate(); }, onError: (e) => toast.error(describeError(e, t)) });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/api/tasks/topics/${id}`), onSuccess: () => { toast.success(t('tasks.topicDeleted')); invalidate(); }, onError: (e) => toast.error(describeError(e, t)) });
  const submit = () => {
    const body = { name_en: form.name_en, name_ar: form.name_ar, description: form.description || null, colour: form.colour };
    if (editing === 'new') create.mutate({ ...body, sort_order: 0 });
    else if (editing) update.mutate({ id: editing.id, b: body });
  };
  return (
    <Modal open onClose={onClose} title={t('tasks.topics')} wide footer={<button type="button" className="btn-secondary" onClick={onClose}>{t('app.close')}</button>}>
      {canEdit && <button type="button" className="btn-primary mb-3" onClick={() => { setForm({ name_en: '', name_ar: '', description: '', colour: '#6366f1' }); setEditing('new'); }}><Plus size={14} /> {t('tasks.newTopic')}</button>}
      <table className="w-full text-sm">
        <thead><tr className="text-xs uppercase text-neutral-500"><th className="py-1 text-start">{t('users.nameEn')}</th><th className="py-1 text-start">{t('users.nameAr')}</th><th className="py-1 text-start">{t('tasks.taskCount')}</th><th /></tr></thead>
        <tbody>
          {(topics.data?.items ?? []).map((tp) => (
            <tr key={tp.id} className="border-t border-neutral-100 dark:border-neutral-800">
              <td className="py-1"><span className="me-2 inline-block h-3 w-3 rounded-full align-middle" style={{ background: tp.colour }} />{tp.name_en}{!tp.is_active && <Badge tone="danger">{t('users.inactive')}</Badge>}</td>
              <td className="py-1" dir="rtl">{tp.name_ar}</td>
              <td className="py-1">{tp.task_count}</td>
              <td className="py-1 text-end">
                {canEdit && <button type="button" className="btn-secondary px-2 py-1" onClick={() => { setForm({ name_en: tp.name_en, name_ar: tp.name_ar, description: tp.description ?? '', colour: tp.colour }); setEditing(tp); }}>{t('app.edit')}</button>}
                {canDelete && <button type="button" className="btn-secondary ms-1 px-2 py-1" onClick={() => setToDelete(tp)} aria-label={t('app.delete')}><Trash2 size={14} /></button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Modal open={editing !== null} onClose={() => setEditing(null)} title={editing === 'new' ? t('tasks.newTopic') : t('app.edit')} footer={<><button type="button" className="btn-secondary" onClick={() => setEditing(null)}>{t('app.cancel')}</button><button type="button" className="btn-primary" disabled={!form.name_en || !form.name_ar} onClick={submit}>{t('app.save')}</button></>}>
        <div className="grid gap-3">
          <Field label={t('users.nameEn')} required><input className="input" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} /></Field>
          <Field label={t('users.nameAr')} required><input className="input" dir="rtl" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} /></Field>
          <Field label={t('parameters.description')}><input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <Field label={t('permissions.colour')}><input type="color" className="input h-10 w-24 p-1" value={form.colour} onChange={(e) => setForm({ ...form, colour: e.target.value })} /></Field>
        </div>
      </Modal>
      <ConfirmDialog open={toDelete !== null} title={t('tasks.deleteTopic')} body={toDelete ? t('tasks.deleteTopicBody', { count: toDelete.task_count }) : undefined} danger onCancel={() => setToDelete(null)} onConfirm={() => { if (toDelete) remove.mutate(toDelete.id); setToDelete(null); }} />
    </Modal>
  );
}
